///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Drop-pin context menu, opened by long-press / right-click on the globe
/// (EarthDropPinHandler). Offers creating a placemark at the pressed spot
/// (named for the nearest city when one is close, coordinates otherwise)
/// or adding the spot as a member location of a recent existing placemark.
///

import { coordsLabel, nearestPlaceName } from "@/utils/nearestPlace"
import { setDropPinMenu } from "@/redux/dialogSlice"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import {
	addLocations,
	cDefaultPlacemarkColor,
	cMaxPlacemarkLocations,
	upsertPlacemark,
	type Placemark,
} from "@/redux/placemarksSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { MapPin, Plus } from "lucide-react"
import { useEffect, useState } from "react"

const cMenuWidth = 240
const cMaxExistingShown = 4

export function DropPinMenu() {
	const dispatch = useAppDispatch()
	const menu = useAppSelector(state => state.dialog.dropPinMenu)
	const placemarks = useAppSelector(state => state.placemarks.placemarks)
	const [placeName, setPlaceName] = useState<string | null>(null)

	// Reverse-geocode the pressed spot for a friendly default name
	useEffect(() => {
		setPlaceName(null)
		if (!menu) return
		let cancelled = false
		void nearestPlaceName(menu.lat, menu.lon).then(name => {
			if (!cancelled) setPlaceName(name)
		})
		return () => { cancelled = true }
	}, [menu])

	useEffect(() => {
		if (!menu) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") dispatch(setDropPinMenu(null))
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	}, [menu, dispatch])

	if (!menu) return null

	const name = placeName ?? coordsLabel(menu.lat, menu.lon)

	// Most recently touched placemarks with member room left
	const existing = Object.values(placemarks)
		.filter(placemark => placemark.locations.length < cMaxPlacemarkLocations)
		.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
		.slice(0, cMaxExistingShown)

	const close = () => dispatch(setDropPinMenu(null))

	const createPlacemark = () => {
		const now = new Date().toISOString()
		const id = crypto.randomUUID()
		dispatch(upsertPlacemark({
			id,
			name,
			locations: [{ id: crypto.randomUUID(), lat: menu.lat, lon: menu.lon }],
			color: cDefaultPlacemarkColor,
			tags: [],
			createdAt: now,
			updatedAt: now,
		}))
		dispatch(sendDatasetAction({ type: "placemark", action: "enable" }))
		dispatch(sendDatasetAction({ type: "placemark", action: "selectId", id }))
		close()
	}

	const addToPlacemark = (placemark: Placemark) => {
		dispatch(addLocations({
			id: placemark.id,
			locations: [{
				id: crypto.randomUUID(),
				lat: menu.lat,
				lon: menu.lon,
				name: placeName ?? undefined,
			}],
		}))
		dispatch(sendDatasetAction({ type: "placemark", action: "enable" }))
		close()
	}

	// Keep the menu on screen (open toward the top-left when near the edges)
	const left = Math.min(menu.x, window.innerWidth - cMenuWidth - 8)
	const clampedTop = Math.min(menu.y, window.innerHeight - 90 - existing.length * 32)

	return (
		<>
			{/* Click-away backdrop */}
			<div
				className="fixed inset-0 z-40"
				onPointerDown={close}
				onContextMenu={event => { event.preventDefault(); close() }}
			/>
			<div
				role="menu"
				aria-label="Drop pin menu"
				className="fixed z-50 rounded-xl border border-slate-700 bg-black/70 backdrop-blur-md shadow-xl p-1.5 text-sm text-white"
				style={{ left, top: clampedTop, width: cMenuWidth }}
			>
				<div className="px-2.5 pt-1.5 pb-2 text-xs text-slate-400 truncate">
					{name}
				</div>
				<button
					type="button"
					role="menuitem"
					onClick={createPlacemark}
					className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-white/10 transition-colors"
				>
					<MapPin className="h-4 w-4 shrink-0 text-red-400" aria-hidden />
					<span className="truncate">Add placemark</span>
				</button>
				{existing.length > 0 && (
					<>
						<div className="mx-2.5 my-1 border-t border-slate-700" />
						<div className="px-2.5 pb-1 text-[10px] uppercase tracking-wide text-slate-500">
							Add to existing
						</div>
						{existing.map(placemark => (
							<button
								key={placemark.id}
								type="button"
								role="menuitem"
								onClick={() => addToPlacemark(placemark)}
								className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-white/10 transition-colors"
							>
								<Plus className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
								<span className="truncate">{placemark.name}</span>
								<span className="ml-auto shrink-0 text-xs text-slate-500">
									{placemark.locations.length}
								</span>
							</button>
						))}
					</>
				)}
			</div>
		</>
	)
}
